import { useBrandIdentity } from "../../hooks/useBrandIdentity";
import { cn } from "../../lib/utils";
import reveshareLogo from "../../assets/logo.svg";

interface BrandMarkProps {
  /** Hide the name to get the logo/initial chip alone (collapsed sidebar). */
  showName?: boolean;
  className?: string;
  logoClassName?: string;
  nameClassName?: string;
}

/**
 * The tenant brand's logo + name. Falls back to a brand-colored initial
 * avatar when no logo is uploaded — never to the Reveshare mark, which
 * appears only in <PoweredByReveshare/>.
 */
export function BrandMark({
  showName = true,
  className,
  logoClassName,
  nameClassName,
}: BrandMarkProps) {
  const { name, logo } = useBrandIdentity();
  const displayName = name ?? "Your brand";
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <div className={cn("flex items-center gap-2 min-w-0", className)}>
      {logo ? (
        <img
          src={logo}
          alt={displayName}
          className={cn(
            "w-8 h-8 rounded-lg object-cover shrink-0",
            logoClassName
          )}
        />
      ) : (
        <span
          className={cn(
            "flex w-8 h-8 shrink-0 items-center justify-center rounded-lg bg-brand-100 text-sm font-bold text-brand-700",
            logoClassName
          )}
          aria-hidden
        >
          {initial}
        </span>
      )}
      {showName && (
        <span
          className={cn(
            "truncate font-semibold text-foreground",
            nameClassName
          )}
        >
          {displayName}
        </span>
      )}
    </div>
  );
}

/** Small attribution mark. `collapsed` renders the icon alone. */
export function PoweredByReveshare({
  collapsed = false,
  className,
}: {
  collapsed?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn("flex items-center justify-center gap-1.5 py-1", className)}
      title="Powered by Reveshare"
    >
      <img
        src={reveshareLogo}
        alt="Reveshare"
        className="h-3.5 w-3.5 dark:invert"
      />
      {!collapsed && (
        <span className="whitespace-nowrap text-[11px] text-muted-foreground">
          Powered by <span className="font-semibold">Reveshare</span>
        </span>
      )}
    </div>
  );
}
